import React from "react";
import { IndexForms } from "@hex-insights/app-modules";
import { Button, If } from "@hex-insights/core";
import {
	AddSubFormButtonRenderProps,
	Form,
	NumberField,
	SelectField,
	SubFormField,
	SubFormRenderProps,
} from "@hex-insights/forms";
import { useClassYearLookupQuery } from "../../../Utilities";
import { ClassYearFormat } from "../../Format";
import {
	PersonFormValues,
	RelationshipFormValues,
	StudentAcademicTermEnrollmentFormValues,
	StudentCourseSectionEnrollmentFormValues,
	StudentFormValues,
	StudentHomeRoomSectionEnrollmentFormValues,
} from "../../FormUtilities";
import { ClassYearOrderFormValues } from "../../Order";
import { PersonForm, PersonSelectField } from "../Person";
import { RelationshipForm } from "../Relationship";
import { BaseFieldProps } from "../shared";
import { StudentAcademicTermEnrollmentForm } from "../StudentAcademicTermEnrollment";
import { StudentCourseSectionEnrollmentForm } from "../StudentCourseSectionEnrollment";
import { StudentHomeRoomSectionEnrollmentForm } from "../StudentHomeRoomSectionEnrollment";

/**
 * Generic props for fields of the Student model.
 */
type FieldProps<K extends keyof StudentFormValues.Base = keyof StudentFormValues.Base> = BaseFieldProps<
	Pick<StudentFormValues.Base, K>
>;

/**
 * Generic props for fields of the Student model that only appear in the create form.
 */
type CreateFieldProps<K extends keyof StudentFormValues.Create = keyof StudentFormValues.Create> = BaseFieldProps<
	Pick<StudentFormValues.Create, K>
>;

/**
 * Generic props for fields of the Student model that only appear in the detail form.
 */
type DetailFieldProps<K extends keyof StudentFormValues.Detail = keyof StudentFormValues.Detail> = BaseFieldProps<
	Pick<StudentFormValues.Detail, K>
>;

/**
 * Field component for the `graduationYear` field of the Student model.
 */
export function GraduationYear({ formState, label = "Class Year", ...props }: FieldProps<"graduationYear">) {
	const classYearOrder = IndexForms.useOrderInput(ClassYearOrderFormValues.initial);
	const { loading, data, refetch } = useClassYearLookupQuery({
		variables: { order: classYearOrder },
	});

	const graduationYearOptions = React.useMemo(
		() =>
			data?.classYearConnection.edges.map((e) => ({
				label: ClassYearFormat.name(e.node),
				value: e.node.graduationYear,
			})) ?? [],
		[data],
	);
	const refetchOptions = React.useCallback(() => refetch(), [refetch]);

	return (
		<SelectField
			formState={formState}
			name="graduationYear"
			label={label}
			isLoading={loading}
			options={graduationYearOptions}
			blankValue={null}
			refetchOptions={refetchOptions}
			{...props}
		/>
	);
}

/**
 * Field component for the `yearsFromGraduation` field of the Student model.
 */
export function YearsFromGraduation({ formState, ...props }: DetailFieldProps<"yearsFromGraduation">) {
	return <NumberField formState={formState} name="yearsFromGraduation" validationUnit={1} {...props} />;
}

export namespace Person {
	export type SelectProps = DetailFieldProps<"personID"> &
		Omit<PersonSelectField.SelectProps<StudentFormValues.Base>, "formState" | "name" | "optional">;

	/**
	 * Field component for the `person` edge of the Student model.
	 */
	export function Select({ formState, ...props }: SelectProps) {
		return <PersonSelectField.Select formState={formState} name="personID" {...props} />;
	}

	/**
	 * SubForm field component for the `person` edge of the Student model.
	 */
	export function SubForm({ formState, noHeader = true, style, ...props }: CreateFieldProps<"person">) {
		return (
			<SubFormField
				formState={formState}
				name="person"
				blankItem={PersonFormValues.blankItemInStudent}
				minItems={1}
				maxItems={1}
				noHeader={noHeader}
				style={{ "--sub-form-field__value---padding": 0, ...style } as React.CSSProperties}
				{...props}
			>
				{SubForm.SubForm}
				{SubForm.AddButton}
			</SubFormField>
		);
	}

	SubForm.SubForm = function ({
		formState,
		onRemoveClick,
		canRemoveItem,
	}: SubFormRenderProps<PersonFormValues.CreateInStudent>) {
		return (
			<Form.Column style={{ width: "100%" }}>
				<If condition={canRemoveItem}>
					<Button variant="tertiary" size="small" onClick={onRemoveClick} disabled={formState.disabled}>
						Remove Person
					</Button>
				</If>
				<PersonForm.CreateFieldsInStudent formState={formState} />
			</Form.Column>
		);
	};

	SubForm.AddButton = function ({
		onClick,
		disabled,
		canAddItem,
	}: AddSubFormButtonRenderProps<Pick<StudentFormValues.Create, "person">, PersonFormValues.CreateInStudent>) {
		return (
			<If condition={canAddItem}>
				<Button variant="tertiary" size="small" onClick={onClick} disabled={disabled}>
					Add Person
				</Button>
			</If>
		);
	};
}

export namespace Relationships {
	/**
	 * SubForm field component for the `relationships` edge of the Student model.
	 */
	export function SubForm({ formState, ...props }: CreateFieldProps<"relationships">) {
		return (
			<SubFormField
				formState={formState}
				name="relationships"
				blankItem={RelationshipFormValues.blankItemInStudent}
				{...props}
			>
				{SubForm.SubForm}
				{SubForm.AddButton}
			</SubFormField>
		);
	}

	SubForm.SubForm = function ({
		formState,
		onRemoveClick,
	}: SubFormRenderProps<RelationshipFormValues.CreateInStudent>) {
		return (
			<Form.Column>
				<Button variant="tertiary" size="small" onClick={onRemoveClick} disabled={formState.disabled}>
					Remove Relationship
				</Button>
				<RelationshipForm.CreateFieldsInStudent formState={formState} />
			</Form.Column>
		);
	};

	SubForm.AddButton = function ({
		onClick,
		disabled,
	}: AddSubFormButtonRenderProps<
		Pick<StudentFormValues.Create, "relationships">,
		RelationshipFormValues.CreateInStudent
	>) {
		return (
			<Button variant="tertiary" size="small" onClick={onClick} disabled={disabled}>
				Add Relationship
			</Button>
		);
	};
}

export namespace StudentAcademicTermEnrollments {
	/**
	 * SubForm field component for the `studentAcademicTermEnrollments` edge of the Student model.
	 */
	export function SubForm({ formState, ...props }: CreateFieldProps<"studentAcademicTermEnrollments">) {
		return (
			<SubFormField
				formState={formState}
				name="studentAcademicTermEnrollments"
				blankItem={StudentAcademicTermEnrollmentFormValues.blankItemInStudent}
				{...props}
			>
				{SubForm.SubForm}
				{SubForm.AddButton}
			</SubFormField>
		);
	}

	SubForm.SubForm = function ({
		formState,
		onRemoveClick,
	}: SubFormRenderProps<StudentAcademicTermEnrollmentFormValues.CreateInStudent>) {
		return (
			<Form.Column>
				<Button variant="tertiary" size="small" onClick={onRemoveClick} disabled={formState.disabled}>
					Remove Student Academic Term Enrollment
				</Button>
				<StudentAcademicTermEnrollmentForm.CreateFieldsInStudent formState={formState} />
			</Form.Column>
		);
	};

	SubForm.AddButton = function ({
		onClick,
		disabled,
	}: AddSubFormButtonRenderProps<
		Pick<StudentFormValues.Create, "studentAcademicTermEnrollments">,
		StudentAcademicTermEnrollmentFormValues.CreateInStudent
	>) {
		return (
			<Button variant="tertiary" size="small" onClick={onClick} disabled={disabled}>
				Add Student Academic Term Enrollment
			</Button>
		);
	};
}

export namespace StudentCourseSectionEnrollments {
	/**
	 * SubForm field component for the `studentCourseSectionEnrollments` edge of the Student model.
	 */
	export function SubForm({ formState, ...props }: CreateFieldProps<"studentCourseSectionEnrollments">) {
		return (
			<SubFormField
				formState={formState}
				name="studentCourseSectionEnrollments"
				blankItem={StudentCourseSectionEnrollmentFormValues.blankItemInStudent}
				{...props}
			>
				{SubForm.SubForm}
				{SubForm.AddButton}
			</SubFormField>
		);
	}

	SubForm.SubForm = function ({
		formState,
		onRemoveClick,
	}: SubFormRenderProps<StudentCourseSectionEnrollmentFormValues.CreateInStudent>) {
		return (
			<Form.Column>
				<Button variant="tertiary" size="small" onClick={onRemoveClick} disabled={formState.disabled}>
					Remove Student Course Section Enrollment
				</Button>
				<StudentCourseSectionEnrollmentForm.CreateFieldsInStudent formState={formState} />
			</Form.Column>
		);
	};

	SubForm.AddButton = function ({
		onClick,
		disabled,
	}: AddSubFormButtonRenderProps<
		Pick<StudentFormValues.Create, "studentCourseSectionEnrollments">,
		StudentCourseSectionEnrollmentFormValues.CreateInStudent
	>) {
		return (
			<Button variant="tertiary" size="small" onClick={onClick} disabled={disabled}>
				Add Student Course Section Enrollment
			</Button>
		);
	};
}

export namespace StudentHomeRoomSectionEnrollments {
	/**
	 * SubForm field component for the `studentHomeRoomSectionEnrollments` edge of the Student model.
	 */
	export function SubForm({ formState, ...props }: CreateFieldProps<"studentHomeRoomSectionEnrollments">) {
		return (
			<SubFormField
				formState={formState}
				name="studentHomeRoomSectionEnrollments"
				blankItem={StudentHomeRoomSectionEnrollmentFormValues.blankItemInStudent}
				{...props}
			>
				{SubForm.SubForm}
				{SubForm.AddButton}
			</SubFormField>
		);
	}

	SubForm.SubForm = function ({
		formState,
		onRemoveClick,
	}: SubFormRenderProps<StudentHomeRoomSectionEnrollmentFormValues.CreateInStudent>) {
		return (
			<Form.Column>
				<Button variant="tertiary" size="small" onClick={onRemoveClick} disabled={formState.disabled}>
					Remove Student Home Room Section Enrollment
				</Button>
				<StudentHomeRoomSectionEnrollmentForm.CreateFieldsInStudent formState={formState} />
			</Form.Column>
		);
	};

	SubForm.AddButton = function ({
		onClick,
		disabled,
	}: AddSubFormButtonRenderProps<
		Pick<StudentFormValues.Create, "studentHomeRoomSectionEnrollments">,
		StudentHomeRoomSectionEnrollmentFormValues.CreateInStudent
	>) {
		return (
			<Button variant="tertiary" size="small" onClick={onClick} disabled={disabled}>
				Add Student Home Room Section Enrollment
			</Button>
		);
	};
}
